import { useState } from 'react'
import { router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { useBasePath } from '@/lib/paths'
import {
  BusinessShareClass,
  BusinessShareholder,
  BusinessCapTableTransaction,
  CapTableSummary
} from '@/types'
import { Plus, Pencil, Trash2, ArrowRightLeft, TrendingUp, Ban } from 'lucide-react'
import { CapTableShareClassSheet } from '../../../components/businesses/CapTableShareClassSheet'
import { CapTableShareholderSheet } from '../../../components/businesses/CapTableShareholderSheet'
import { CapTableTransactionSheet } from '../../../components/businesses/CapTableTransactionSheet'

type Props = {
  business: { id: string; name: string }
  share_classes: BusinessShareClass[]
  shareholders: BusinessShareholder[]
  transactions: BusinessCapTableTransaction[]
  summary: CapTableSummary
}

function TransactionTypeBadge({ type }: { type: string }) {
  const config: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
    issuance: { label: 'Issuance', className: 'bg-green-100 text-green-800', icon: <TrendingUp className="w-3 h-3" /> },
    transfer: { label: 'Transfer', className: 'bg-blue-100 text-blue-800', icon: <ArrowRightLeft className="w-3 h-3" /> },
    cancellation: { label: 'Cancellation', className: 'bg-red-100 text-red-800', icon: <Ban className="w-3 h-3" /> },
  }
  const { label, className, icon } = config[type] || { label: type, className: 'bg-secondary text-secondary-foreground', icon: null }
  return <Badge className={`font-normal text-xs gap-1 ${className}`}>{icon}{label}</Badge>
}

function formatNumber(n: number) {
  return n.toLocaleString()
}

function formatCents(cents: number | null, currency: string = 'usd') {
  if (cents == null) return '—'
  return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`
}

export default function CapTableShow({ business, share_classes, shareholders, transactions, summary }: Props) {
  const basePath = useBasePath()
  const confirm = useConfirm()

  const [shareClassSheetOpen, setShareClassSheetOpen] = useState(false)
  const [editShareClass, setEditShareClass] = useState<BusinessShareClass | null>(null)

  const [shareholderSheetOpen, setShareholderSheetOpen] = useState(false)
  const [editShareholder, setEditShareholder] = useState<BusinessShareholder | null>(null)

  const [transactionSheetOpen, setTransactionSheetOpen] = useState(false)

  return (
    <DashboardLayout
      title="Cap Table"
      breadcrumbs={[
        { label: 'Businesses', href: 'businesses' },
        { label: business.name, href: `businesses/${business.id}` },
        { label: 'Cap Table' }
      ]}
    >
      <div>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Cap Table</h1>
          <p className="text-sm text-muted-foreground mt-1">{business.name}</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="border rounded-lg px-4 py-3">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Authorized</div>
            <div className="text-lg font-semibold mt-1">{formatNumber(summary.total_shares_authorized)}</div>
          </div>
          <div className="border rounded-lg px-4 py-3">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Issued</div>
            <div className="text-lg font-semibold mt-1">{formatNumber(summary.total_shares_issued)}</div>
          </div>
          <div className="border rounded-lg px-4 py-3">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Outstanding</div>
            <div className="text-lg font-semibold mt-1">{formatNumber(summary.total_shares_outstanding)}</div>
          </div>
          <div className="border rounded-lg px-4 py-3">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Shareholders</div>
            <div className="text-lg font-semibold mt-1">{formatNumber(summary.total_shareholders)}</div>
          </div>
        </div>

        {/* Share Classes Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Share Classes</h2>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setEditShareClass(null); setShareClassSheetOpen(true) }}>
              <Plus className="w-3 h-3 mr-1" />
              Add
            </Button>
          </div>
          {share_classes.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Name</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Authorized</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Issued</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Outstanding</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Par Value</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Currency</th>
                    <th className="w-20 px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {share_classes.map(sc => (
                    <tr key={sc.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium">{sc.name}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatNumber(sc.shares_authorized)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatNumber(sc.shares_issued)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatNumber(sc.shares_outstanding)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{formatCents(sc.par_value_cents, sc.currency)}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground uppercase">{sc.currency}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditShareClass(sc); setShareClassSheetOpen(true) }}>
                            <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={async () => {
                            if (await confirm({ description: `Delete "${sc.name}"? This cannot be undone.`, confirmLabel: 'Delete', destructive: true })) {
                              router.delete(`${basePath}/businesses/${business.id}/share_classes/${sc.id}`)
                            }
                          }}>
                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="border border-dashed rounded-lg px-6 py-8 text-center bg-muted/30">
              <p className="text-sm text-muted-foreground mb-3">No share classes yet</p>
              <Button size="sm" variant="outline" onClick={() => { setEditShareClass(null); setShareClassSheetOpen(true) }}>
                <Plus className="w-3 h-3 mr-1" />
                Add Share Class
              </Button>
            </div>
          )}
        </div>

        {/* Shareholders Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Shareholders</h2>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setEditShareholder(null); setShareholderSheetOpen(true) }}>
              <Plus className="w-3 h-3 mr-1" />
              Add
            </Button>
          </div>
          {shareholders.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Name</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Type</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Total Shares</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Ownership %</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Holdings</th>
                    <th className="w-20 px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {shareholders.map(sh => (
                    <tr key={sh.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium">{sh.name}</div>
                        {sh.email && <div className="text-xs text-muted-foreground">{sh.email}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary" className="font-normal text-xs capitalize">{sh.shareholder_type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatNumber(sh.total_shares)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{sh.ownership_percentage}%</td>
                      <td className="px-4 py-3">
                        {sh.holdings.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {sh.holdings.map(h => (
                              <Badge key={h.share_class_id} variant="outline" className="font-normal text-xs">
                                {h.share_class_name}: {formatNumber(h.shares)} ({h.percentage_of_class}%)
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditShareholder(sh); setShareholderSheetOpen(true) }}>
                            <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={async () => {
                            if (await confirm({ description: `Remove "${sh.name}"? This cannot be undone.`, confirmLabel: 'Remove', destructive: true })) {
                              router.delete(`${basePath}/businesses/${business.id}/shareholders/${sh.id}`)
                            }
                          }}>
                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="border border-dashed rounded-lg px-6 py-8 text-center bg-muted/30">
              <p className="text-sm text-muted-foreground mb-3">No shareholders yet</p>
              <Button size="sm" variant="outline" onClick={() => { setEditShareholder(null); setShareholderSheetOpen(true) }}>
                <Plus className="w-3 h-3 mr-1" />
                Add Shareholder
              </Button>
            </div>
          )}
        </div>

        {/* Transactions Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Transactions</h2>
            {share_classes.length > 0 && shareholders.length > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setTransactionSheetOpen(true)}>
                <Plus className="w-3 h-3 mr-1" />
                Record
              </Button>
            )}
          </div>
          {transactions.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Type</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Shareholder</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">From</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Share Class</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Shares</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Price/Share</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(t => (
                    <tr key={t.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-muted-foreground tabular-nums">{t.effective_date}</td>
                      <td className="px-4 py-3"><TransactionTypeBadge type={t.transaction_type} /></td>
                      <td className="px-4 py-3 font-medium">{t.shareholder_name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{t.from_shareholder_name || '—'}</td>
                      <td className="px-4 py-3">{t.share_class_name}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{formatNumber(t.shares)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{formatCents(t.price_per_share_cents, t.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="border border-dashed rounded-lg px-6 py-8 text-center bg-muted/30">
              <p className="text-sm text-muted-foreground mb-3">No transactions recorded yet</p>
              {share_classes.length > 0 && shareholders.length > 0 ? (
                <Button size="sm" variant="outline" onClick={() => setTransactionSheetOpen(true)}>
                  <Plus className="w-3 h-3 mr-1" />
                  Record Transaction
                </Button>
              ) : (
                <p className="text-xs text-muted-foreground">Add share classes and shareholders first</p>
              )}
            </div>
          )}
        </div>
      </div>

      <CapTableShareClassSheet
        open={shareClassSheetOpen}
        onOpenChange={setShareClassSheetOpen}
        businessId={business.id}
        shareClass={editShareClass}
        basePath={basePath}
      />
      <CapTableShareholderSheet
        open={shareholderSheetOpen}
        onOpenChange={setShareholderSheetOpen}
        businessId={business.id}
        shareholder={editShareholder}
        basePath={basePath}
      />
      <CapTableTransactionSheet
        open={transactionSheetOpen}
        onOpenChange={setTransactionSheetOpen}
        businessId={business.id}
        basePath={basePath}
        shareClasses={share_classes}
        shareholders={shareholders}
      />
    </DashboardLayout>
  )
}
